import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useRouteLoaderData, useRevalidator } from "react-router";
import type { FetcherWithComponents } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

import { supabase } from "@shared/integrations/supabase/client";

import { TestRunsTable } from "@/components/shared/TestRunsTable";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertTriangle,
  PlayCircle,
  Sparkles,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useFetcher } from "react-router";
import {
  WORKSPACE_SIDEBAR_ACTION_PATH,
  type WorkspaceSidebarActionResult,
} from "@/routes/workspaceSidebarAction";
import {
  WORKSPACE_LOADER_ID,
  type WorkspaceLoaderData,
} from "@/routes/workspaceLoader";
import type {
  PlaygroundLoaderData,
  PlaygroundTestRun,
} from "@/routes/playgroundLoader";
import type { TestRunResultsLoaderData } from "@/routes/testRunsLoader";
import { CreateDerivativeDialog } from "@/components/CreateDerivativeDialog";

interface PlaygroundViewProps {
  projectId: string;
  playgroundData: PlaygroundLoaderData;
  onQueuePrimaryRun: (
    datasetId: string,
    evalType: string,
    judgeId: string | null,
    permittedValues?: string,
  ) => void;
  onQueueDerivativeRun: (
    datasetId: string,
    evalType: string,
    judgeId: string | null,
    permittedValues?: string,
  ) => void;
  testRunCreationFetcher?: FetcherWithComponents<WorkspaceSidebarActionResult>;
  readOnly?: boolean;
}

export const PlaygroundView = ({
  projectId,
  playgroundData,
  onQueuePrimaryRun,
  onQueueDerivativeRun,
  testRunCreationFetcher,
  readOnly = false,
}: PlaygroundViewProps) => {
  // Run settings (applies to all runs)
  const [evalType, setEvalType] = useState("Strict Equality");
  const [judgeId, setJudgeId] = useState<string | null>(null);
  const [permittedValues, setPermittedValues] = useState("");

  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(
    null,
  );

  const [showResults, setShowResults] = useState(false);
  const [selectedTestRunId, setSelectedTestRunId] = useState<string | null>(
    null,
  );
  const [resultsCurrentPage, setResultsCurrentPage] = useState(1);
  const [derivativeRunTimestamps, setDerivativeRunTimestamps] = useState<
    Map<string, string>
  >(new Map());

  // Adversarial dialog state
  const [showDerivativeDialog, setShowDerivativeDialog] = useState(false);
  const [derivativeDialogParent, setDerivativeDialogParent] = useState<{
    id: string;
    name: string;
    dataset_kind: string;
  } | null>(null);

  const { toast } = useToast();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const layoutData = useRouteLoaderData(
    WORKSPACE_LOADER_ID,
  ) as WorkspaceLoaderData;
  const judges = layoutData.judges;
  const [datasets, setDatasets] = useState(layoutData.datasets);
  const playgroundFetcher = useFetcher<PlaygroundLoaderData>();
  const launchFetcher = useFetcher<WorkspaceSidebarActionResult>();
  const deleteFetcher = useFetcher<WorkspaceSidebarActionResult>();
  const resultsFetcher = useFetcher<TestRunResultsLoaderData>();
  const lastHandledCreateId = useRef<string | null>(null);
  const lastHandledLaunchId = useRef<string | null>(null);
  const lastHandledDeleteId = useRef<string | null>(null);
  const latestPlaygroundData = playgroundFetcher.data ?? playgroundData;
  const isRefreshing = playgroundFetcher.state !== "idle";
  const hasAssignedAgent = Boolean(
    latestPlaygroundData.project.codeId ?? latestPlaygroundData.project.agentId,
  );

  useEffect(() => {
    setDatasets(layoutData.datasets);
  }, [layoutData.datasets]);

  const addDatasetToState = useCallback(
    async (datasetId: string) => {
      const { data, error } = await supabase
        .from("test_data_sets")
        .select("*")
        .eq("id", datasetId)
        .single();

      if (error || !data) {
        return;
      }

      const ownerEmail =
        layoutData.profile?.display_name ??
        layoutData.user.email ??
        "Unknown";

      setDatasets((current) =>
        current.some((dataset) => dataset.id === data.id)
          ? current
          : [{ ...data, owner_email: ownerEmail }, ...current],
      );
    },
    [layoutData.profile?.display_name, layoutData.user.email],
  );

  // All primary datasets (both behavior and rules)
  const primaryDatasets = useMemo(
    () => datasets.filter((dataset) => dataset.type === "primary"),
    [datasets],
  );

  // Primary datasets split by kind for dropdown grouping
  const primaryBehaviorDatasets = useMemo(
    () =>
      primaryDatasets.filter(
        (dataset) => dataset.dataset_kind === "behavior_examples",
      ),
    [primaryDatasets],
  );

  const primaryRulesDatasets = useMemo(
    () =>
      primaryDatasets.filter(
        (dataset) => dataset.dataset_kind === "operational_rules",
      ),
    [primaryDatasets],
  );

  const fetchPlaygroundData = useCallback(() => {
    playgroundFetcher.load(`/workspace/evals/${projectId}/playground`);
  }, [playgroundFetcher, projectId]);

  // Track adversarial run timestamps
  useEffect(() => {
    const timestampsMap = new Map<string, string>();
    latestPlaygroundData.testRuns.forEach((run) => {
      if (run.test_data_set_id) {
        const existingTimestamp = timestampsMap.get(run.test_data_set_id);
        if (
          !existingTimestamp ||
          new Date(run.start_time) > new Date(existingTimestamp)
        ) {
          timestampsMap.set(run.test_data_set_id, run.start_time);
        }
      }
    });
    setDerivativeRunTimestamps(timestampsMap);
  }, [latestPlaygroundData.testRuns]);

  const completedPrimaryRunByDataset = useMemo(() => {
    const map = new Map<string, PlaygroundTestRun>();
    latestPlaygroundData.testRuns.forEach((run) => {
      if (
        run.test_data_set_id &&
        run.type === "primary" &&
        run.status === "Complete" &&
        !map.has(run.test_data_set_id)
      ) {
        map.set(run.test_data_set_id, run);
      }
    });
    return map;
  }, [latestPlaygroundData.testRuns]);

  // Validate judge ID when judges change
  useEffect(() => {
    if (judges.length === 0) {
      if (judgeId !== null) setJudgeId(null);
      return;
    }

    const fallbackJudgeId = judges[0]?.id ?? null;
    if (judgeId && !judges.some((judge) => judge.id === judgeId)) {
      setJudgeId(fallbackJudgeId);
    }
  }, [judges, judgeId]);

  // Ensure dataset selection is valid
  useEffect(() => {
    if (primaryDatasets.length === 0) {
      if (selectedDatasetId !== null) setSelectedDatasetId(null);
      return;
    }

    if (
      selectedDatasetId &&
      primaryDatasets.some((dataset) => dataset.id === selectedDatasetId)
    ) {
      return;
    }

    // Pick first available primary dataset
    const nextId = primaryDatasets[0]?.id ?? null;
    setSelectedDatasetId(nextId);
  }, [selectedDatasetId, primaryDatasets]);

  useEffect(() => {
    const hasInflightRuns = latestPlaygroundData.testRuns.some(
      (run) => run.status !== "Complete" && run.status !== "Failed",
    );

    if (!hasInflightRuns) {
      return;
    }

    const interval = setInterval(() => {
      fetchPlaygroundData();
    }, 2000);

    return () => {
      clearInterval(interval);
    };
  }, [latestPlaygroundData.testRuns, fetchPlaygroundData]);

  // Manually refresh when parent creates a test run (immediate feedback before next poll)
  useEffect(() => {
    const data = testRunCreationFetcher?.data;

    // Exit early if not idle or no data
    if (testRunCreationFetcher?.state !== "idle" || !data) {
      return;
    }

    // Exit if not a successful testRunCreate
    if (!data.ok || data.intent !== "testRunCreate") {
      return;
    }

    // Skip if we already handled this exact response
    if (data.recordId && lastHandledCreateId.current === data.recordId) {
      console.debug(
        "[PlaygroundView] Skipping duplicate testRunCreate success:",
        data.recordId,
      );
      return;
    }

    console.debug(
      "[PlaygroundView] Test run created successfully:",
      data.recordId,
    );
    lastHandledCreateId.current = data.recordId ?? null;

    // Immediately refresh to show the new queued run
    fetchPlaygroundData();
  }, [
    testRunCreationFetcher?.state,
    testRunCreationFetcher?.data,
    fetchPlaygroundData,
  ]);

  // Handle launch fetcher responses
  useEffect(() => {
    const data = launchFetcher.data;

    // Exit early if not idle or no data
    if (launchFetcher.state !== "idle" || !data) {
      return;
    }

    // Create unique ID for this response to prevent duplicate handling
    const responseId = data.ok ? data.recordId : `${data.intent}-${Date.now()}`;

    // Skip if we already handled this exact response
    if (lastHandledLaunchId.current === responseId) {
      return;
    }

    lastHandledLaunchId.current = responseId;

    if (data.ok) {
      toast({
        title: "Success",
        description:
          "Test run launched successfully. Processing in background...",
      });
      fetchPlaygroundData();
    } else {
      toast({
        title: "Error",
        description: data.error || "Failed to launch test run.",
        variant: "destructive",
      });
    }
  }, [launchFetcher.state, launchFetcher.data, toast, fetchPlaygroundData]);

  // Handle delete fetcher responses
  useEffect(() => {
    const data = deleteFetcher.data;

    // Exit early if not idle or no data
    if (deleteFetcher.state !== "idle" || !data) {
      return;
    }

    // Create unique ID for this response to prevent duplicate handling
    const responseId = data.ok ? data.recordId : `${data.intent}-${Date.now()}`;

    // Skip if we already handled this exact response
    if (lastHandledDeleteId.current === responseId) {
      return;
    }

    lastHandledDeleteId.current = responseId;

    if (data.ok) {
      toast({
        title: "Success",
        description: "Test run deleted successfully.",
      });
      fetchPlaygroundData();
    } else {
      toast({
        title: "Error",
        description: data.error || "Failed to delete test run.",
        variant: "destructive",
      });
    }
  }, [deleteFetcher.state, deleteFetcher.data, toast, fetchPlaygroundData]);

  const selectedDataset =
    datasets.find((dataset) => dataset.id === selectedDatasetId) ?? null;

  // Get the primary dataset (selected dataset itself if primary, or its parent if adversarial)
  const primaryDataset = useMemo(() => {
    if (!selectedDataset) return null;
    if (selectedDataset.type === "primary") return selectedDataset;
    if (!selectedDataset.parent_id) return null;
    return (
      datasets.find((candidate) => candidate.id === selectedDataset.parent_id) ??
      null
    );
  }, [selectedDataset, datasets]);

  // Get derivatives for the currently selected primary dataset
  const derivatives = useMemo(() => {
    if (!primaryDataset?.id) return [];
    return datasets.filter(
      (dataset) =>
        dataset.type === "adversarial" &&
        dataset.parent_id === primaryDataset.id,
    );
  }, [primaryDataset?.id, datasets]);

  const canQueueDerivative = (primaryDatasetId: string | null) => {
    if (!primaryDatasetId) return false;
    return completedPrimaryRunByDataset.has(primaryDatasetId);
  };

  const handleLaunchTestRun = (testRunId: string) => {
    if (readOnly) {
      toast({
        title: "Read-only",
        description: "You don't have permission to launch test runs here.",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData();
    formData.set("intent", "testRunLaunch");
    formData.set("testRunId", testRunId);
    launchFetcher.submit(formData, {
      method: "post",
      action: WORKSPACE_SIDEBAR_ACTION_PATH,
    });
  };

  const handleDeleteTestRun = (testRunId: string) => {
    if (readOnly) {
      toast({
        title: "Read-only",
        description: "You don't have permission to delete test runs here.",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData();
    formData.set("intent", "testRunDelete");
    formData.set("testRunId", testRunId);
    deleteFetcher.submit(formData, {
      method: "post",
      action: WORKSPACE_SIDEBAR_ACTION_PATH,
    });
  };

  const handleRetryTestRun = (testRunId: string) => {
    handleLaunchTestRun(testRunId);
  };

  const isLaunchingRun = (testRunId: string) =>
    launchFetcher.state !== "idle" &&
    launchFetcher.formData?.get("testRunId") === testRunId;

  const isDeletingRun = (testRunId: string) =>
    deleteFetcher.state !== "idle" &&
    deleteFetcher.formData?.get("testRunId") === testRunId;

  const isRetryingRun = isLaunchingRun;

  const handleViewTestResults = (testRunId: string) => {
    setSelectedTestRunId(testRunId);
    setShowResults(true);
    setResultsCurrentPage(1);

    // Fetch results using the loader
    resultsFetcher.load(
      `/workspace/test-runs/results?testRunId=${testRunId}&page=1`,
    );
  };

  const handleBackToTestRuns = () => {
    setShowResults(false);
    setSelectedTestRunId(null);
  };

  // Refetch results when page changes
  useEffect(() => {
    if (showResults && selectedTestRunId) {
      resultsFetcher.load(
        `/workspace/test-runs/results?testRunId=${selectedTestRunId}&page=${resultsCurrentPage}`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsCurrentPage, showResults, selectedTestRunId]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getCorrectnessColor = (correctness: number) => {
    if (correctness >= 90) return "text-green-600 dark:text-green-400";
    if (correctness >= 75) return "text-yellow-600 dark:text-yellow-400";
    return "text-red-600 dark:text-red-400";
  };

  const formatDatasetOption = (dataset: (typeof datasets)[number]) => {
    const parentName = dataset.parent_id
      ? datasets.find((parent) => parent.id === dataset.parent_id)?.name
      : null;
    const suffix = parentName ? ` (from ${parentName})` : "";
    const publicLabel = dataset.is_public ? " (Public)" : "";
    return `${dataset.name}${publicLabel}${suffix}`;
  };

  const isPrimarySelected = selectedDataset?.type === "primary";
  const canQueue = canQueueDerivative(primaryDataset?.id ?? null);
  const showAgentValidation = selectedDataset && !hasAssignedAgent;

  // If showing results, render results view
  if (showResults && selectedTestRunId) {
    const selectedRun = latestPlaygroundData.testRuns.find(
      (run) => run.id === selectedTestRunId,
    );
    const resultsData = resultsFetcher.data;
    const isResultsLoading = resultsFetcher.state === "loading";

    return (
      <div
        className="bg-card border border-border rounded-lg"
        data-testid="playground-results-view"
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToTestRuns}
              className="p-2"
              aria-label="Back to test runs"
              title="Back to test runs"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-foreground">
                Test Results
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Results for {selectedRun?.type} test run from{" "}
                {selectedRun ? formatDate(selectedRun.start_time) : ""}
              </p>
            </div>
          </div>

          {/* Stats Section */}
          {resultsData && resultsData.testResults.length > 0 && (
            <div className="mt-4 p-4 bg-muted/30 rounded-lg border">
              <h4 className="text-sm font-medium text-foreground mb-3">
                Stats
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div
                  className="text-center"
                  data-testid="playground-results-correct"
                >
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {resultsData.stats.correct}%
                  </div>
                  <div className="text-xs text-muted-foreground">Correct</div>
                </div>
                <div
                  className="text-center"
                  data-testid="playground-results-partial"
                >
                  <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                    {resultsData.stats.partial}%
                  </div>
                  <div className="text-xs text-muted-foreground">Partial</div>
                </div>
                <div
                  className="text-center"
                  data-testid="playground-results-incorrect"
                >
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {resultsData.stats.incorrect}%
                  </div>
                  <div className="text-xs text-muted-foreground">Incorrect</div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="p-6">
          {isResultsLoading ? (
            <div className="text-center py-8">Loading results...</div>
          ) : !resultsData || resultsData.testResults.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No test results found.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/4 min-w-[200px]">
                        Input
                      </TableHead>
                      <TableHead className="w-1/4 min-w-[200px]">
                        Expected Output
                      </TableHead>
                      <TableHead className="w-1/4 min-w-[200px]">
                        Actual Output
                      </TableHead>
                      <TableHead className="w-1/4 min-w-[100px]">
                        Correctness
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resultsData.testResults.map((result) => (
                      <TableRow key={result.id}>
                        <TableCell className="text-sm align-top">
                          <div className="max-w-none break-words whitespace-normal">
                            {result.test_data_entries?.input ?? "N/A"}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm align-top">
                          <div className="max-w-none break-words whitespace-normal">
                            {result.test_data_entries?.expected_output ?? "N/A"}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm align-top">
                          <div className="max-w-none break-words whitespace-normal">
                            {result.output}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm align-top">
                          {result.correctness !== null ? (
                            <span
                              className={`font-medium ${getCorrectnessColor(result.correctness)}`}
                            >
                              {result.correctness}%
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              {result.evaluation_status === "pending"
                                ? "Pending"
                                : result.evaluation_status === "in_progress"
                                  ? "Evaluating..."
                                  : result.evaluation_status === "failed"
                                    ? "Failed"
                                    : "N/A"}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {resultsData.totalCount > resultsData.pageSize && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-muted-foreground">
                    Showing{" "}
                    {(resultsCurrentPage - 1) * resultsData.pageSize + 1} to{" "}
                    {Math.min(
                      resultsCurrentPage * resultsData.pageSize,
                      resultsData.totalCount,
                    )}{" "}
                    of {resultsData.totalCount} results
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setResultsCurrentPage(
                          Math.max(1, resultsCurrentPage - 1),
                        )
                      }
                      disabled={resultsCurrentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <span className="text-sm">
                      Page {resultsCurrentPage} of{" "}
                      {Math.ceil(resultsData.totalCount / resultsData.pageSize)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setResultsCurrentPage(
                          Math.min(
                            Math.ceil(
                              resultsData.totalCount / resultsData.pageSize,
                            ),
                            resultsCurrentPage + 1,
                          ),
                        )
                      }
                      disabled={
                        resultsCurrentPage >=
                        Math.ceil(resultsData.totalCount / resultsData.pageSize)
                      }
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="playground-view">
      {/* Test Setup Section */}
      <Card data-testid="playground-test-setup">
        <CardHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-1.5">
                <CardTitle>Test Setup</CardTitle>
                <CardDescription>
                  Select a dataset to start runs.
                </CardDescription>
              </div>
              {selectedDataset && (
                <Badge variant="outline" className="capitalize">
                  {selectedDataset.type}
                </Badge>
              )}
            </div>
            <Select
              value={selectedDataset?.id ?? "none"}
              onValueChange={(value) =>
                setSelectedDatasetId(value === "none" ? null : value)
              }
            >
              <SelectTrigger
                className="w-full"
                data-testid="dataset-select"
              >
                <SelectValue placeholder="Select dataset" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No dataset selected</SelectItem>
                <SelectGroup>
                  <SelectLabel>Behaviors</SelectLabel>
                  {primaryBehaviorDatasets.length === 0 ? (
                    <SelectItem value="none-behavior" disabled>
                      No behavior datasets available
                    </SelectItem>
                  ) : (
                    primaryBehaviorDatasets.map((dataset) => (
                      <SelectItem key={dataset.id} value={dataset.id}>
                        {formatDatasetOption(dataset)}
                      </SelectItem>
                    ))
                  )}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>Rules</SelectLabel>
                  {primaryRulesDatasets.length === 0 ? (
                    <SelectItem value="none-rules" disabled>
                      No rules datasets available
                    </SelectItem>
                  ) : (
                    primaryRulesDatasets.map((dataset) => (
                      <SelectItem key={dataset.id} value={dataset.id}>
                        {formatDatasetOption(dataset)}
                      </SelectItem>
                    ))
                  )}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Run Settings - applies to all runs */}
          {selectedDataset && (
            <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
              <h4 className="text-xs font-medium text-muted-foreground">
                Run Settings
              </h4>

              <div className="space-y-2">
                <label className="text-xs font-medium">Evaluation Type</label>
                <Select value={evalType} onValueChange={setEvalType}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Strict Equality">Strict Equality</SelectItem>
                    <SelectItem value="Any permitted">Permitted Values</SelectItem>
                    <SelectItem value="LLM-As-A-Judge">LLM Judge</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {evalType === "LLM-As-A-Judge" && (
                <div className="space-y-2">
                  <label className="text-xs font-medium">Judge</label>
                  <Select
                    value={judgeId ?? ""}
                    onValueChange={(val) => setJudgeId(val === "none" ? null : val)}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select a judge" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No judge selected</SelectItem>
                      {judges.map((judge) => (
                        <SelectItem key={judge.id} value={judge.id}>
                          {judge.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {evalType === "Any permitted" && (
                <div className="space-y-2">
                  <label className="text-xs font-medium">Permitted Values (comma-separated)</label>
                  <Input
                    value={permittedValues}
                    onChange={(e) => setPermittedValues(e.target.value)}
                    placeholder="yes, no, maybe"
                    className="h-9"
                  />
                </div>
              )}
            </div>
          )}

          {/* Primary Run - only show Queue button for primary datasets */}
          {selectedDataset && isPrimarySelected && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold">Primary Run</h3>
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3 flex-1">
                  <Badge variant="secondary" className="capitalize">
                    {selectedDataset.creation_method}
                  </Badge>
                  <span className="text-sm">{selectedDataset.name}</span>
                </div>
                <Button
                  data-testid="primary-queue-button"
                  onClick={() =>
                    onQueuePrimaryRun(
                      selectedDataset.id,
                      evalType,
                      judgeId,
                      permittedValues,
                    )
                  }
                  disabled={
                    readOnly ||
                    !selectedDataset ||
                    !hasAssignedAgent ||
                    (evalType === "LLM-As-A-Judge" && !judgeId) ||
                    (evalType === "Any permitted" && !permittedValues.trim())
                  }
                  size="sm"
                  variant="outline"
                >
                  <PlayCircle className="h-4 w-4 mr-2" />
                  Queue
                </Button>
              </div>
            </div>
          )}

          {selectedDataset && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">
                  {isPrimarySelected ? "Adversarial Runs" : "Adversarial Run"}
                </h3>
                {isPrimarySelected && canQueue && (
                  <Button
                    onClick={() => {
                      if (primaryDataset) {
                        setDerivativeDialogParent({
                          id: primaryDataset.id,
                          name: primaryDataset.name,
                          dataset_kind: primaryDataset.dataset_kind,
                        });
                        setShowDerivativeDialog(true);
                      }
                    }}
                    variant="outline"
                    size="sm"
                    disabled={readOnly || !primaryDataset}
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate
                  </Button>
                )}
              </div>

              {!canQueue && primaryDataset ? (
                <p className="text-sm text-muted-foreground">
                  Complete a primary run before queuing adversarial runs.
                </p>
              ) : (
                <>

                  {showAgentValidation && (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        Assign an Agent to this Eval before queuing runs.
                      </AlertDescription>
                    </Alert>
                  )}

                  {isPrimarySelected ? (
                    derivatives.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        No adversarial datasets yet. Generate one to test robustness.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {derivatives
                          .map((dataset) => ({
                            dataset,
                            lastRunTime: derivativeRunTimestamps.get(dataset.id),
                          }))
                          .sort((a, b) => {
                            if (!a.lastRunTime && !b.lastRunTime) return 0;
                            if (!a.lastRunTime) return 1;
                            if (!b.lastRunTime) return -1;
                            return (
                              new Date(b.lastRunTime).getTime() -
                              new Date(a.lastRunTime).getTime()
                            );
                          })
                          .map(({ dataset, lastRunTime }) => (
                            <div
                              key={dataset.id}
                              className="flex items-center justify-between p-3 border rounded-lg"
                            >
                              <div className="flex items-center gap-3 flex-1">
                                <Badge variant="secondary" className="capitalize">
                                  {dataset.creation_method}
                                </Badge>
                                <div className="flex flex-col gap-1">
                                  <span className="text-sm">{dataset.name}</span>
                                  {lastRunTime && (
                                    <span className="text-xs text-muted-foreground">
                                      Last run: {formatDate(lastRunTime)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  onClick={() =>
                                    onQueueDerivativeRun(
                                      dataset.id,
                                      evalType,
                                      judgeId,
                                      permittedValues,
                                    )
                                  }
                                  disabled={
                                    readOnly ||
                                    !canQueue ||
                                    !hasAssignedAgent ||
                                    (evalType === "LLM-As-A-Judge" &&
                                      !judgeId) ||
                                    (evalType === "Any permitted" &&
                                      !permittedValues.trim())
                                  }
                                  size="sm"
                                  variant="outline"
                                  data-testid="adversarial-queue-button"
                                >
                                  <PlayCircle className="h-4 w-4 mr-2" />
                                  Queue
                                </Button>
                              </div>
                            </div>
                          ))}
                      </div>
                    )
                  ) : (
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3 flex-1">
                        <Badge variant="secondary" className="capitalize">
                          {selectedDataset.creation_method}
                        </Badge>
                        <span className="text-sm">{selectedDataset.name}</span>
                      </div>
                      <Button
                        onClick={() =>
                          onQueueDerivativeRun(
                            selectedDataset.id,
                            evalType,
                            judgeId,
                            permittedValues,
                          )
                        }
                        disabled={
                          readOnly ||
                          !canQueue ||
                          !hasAssignedAgent ||
                          (evalType === "LLM-As-A-Judge" &&
                            !judgeId) ||
                          (evalType === "Any permitted" &&
                            !permittedValues.trim())
                        }
                        size="sm"
                        variant="outline"
                        data-testid="adversarial-queue-button"
                      >
                        <PlayCircle className="h-4 w-4 mr-2" />
                        Queue
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test Runs Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Test Runs for This Eval</CardTitle>
              <CardDescription>
                View and manage queued test runs
              </CardDescription>
            </div>
            {isRefreshing && (
              <Badge variant="secondary" className="animate-pulse">
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                Updating
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <TestRunsTable
            testRuns={latestPlaygroundData.testRuns}
            onLaunch={handleLaunchTestRun}
            onDelete={handleDeleteTestRun}
            onViewResults={handleViewTestResults}
            onRetry={handleRetryTestRun}
            isLaunching={isLaunchingRun}
            isDeleting={isDeletingRun}
            isRetrying={isRetryingRun}
            readOnly={readOnly}
            contextLabel="Eval"
            tableTestId="playground-test-runs-table"
          />
        </CardContent>
      </Card>

      {/* Adversarial Creation Dialog */}
      {derivativeDialogParent && (
        <CreateDerivativeDialog
          parentDataset={derivativeDialogParent}
          open={showDerivativeDialog}
          onOpenChange={setShowDerivativeDialog}
          onSuccess={(datasetId, isManualCreation) => {
            // Refresh playground data to show new adversarial dataset
            fetchPlaygroundData();
            revalidator.revalidate();
            void addDatasetToState(datasetId);
            setShowDerivativeDialog(false);

            // If manual creation, navigate to dataset detail page to add items
            if (isManualCreation) {
              navigate(`/dataset/${datasetId}`);
            }
          }}
        />
      )}
    </div>
  );
};
